import { FlatTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from '../../services/errorReporting/error-reporting.service';
import { BaseComponent } from 'src/app/base/base.component';
import { DataSourceType } from 'src/app/domainObjects/dataSourceType';
import { FileFlatNode, FileNode } from 'src/app/objectModel/fileNode';
import { BrowseFolderRequest } from 'src/app/messaging/browseFolderRequest';
import { DataConnectorType, FtpService } from 'src/app/domainObjects/dataConnector';
import _ from 'lodash';
import { GetDefaultIngestNodePathRequest } from 'src/app/messaging/getDefaultIngestNodePathRequest';
import { SharedService } from 'src/app/services/shared.service';
import { UserDataService } from 'src/app/services/user-data-service';

class FileSystemItemModel {
  id = 0;
  name = '';
  isFolder = false;
  files: FileSystemItemModel[] | null | undefined;

  constructor(id: number, name: string, isFolder: boolean, files: FileSystemItemModel[] | null) {
    this.id = id;
    this.name = name;
    this.isFolder = isFolder;
    this.files = files;
  }
}

@Component({
    selector: 'app-file-browser-dialog',
    templateUrl: './file-browser-dialog.component.html',
    styleUrls: ['./file-browser-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FileBrowserDialogComponent extends BaseComponent implements OnInit, AfterViewInit {

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        this.dialogRef.close();
        break;
      case 'Enter':
        if (this.focusCycle === 'list' && this.focusedNodeIndex >= 0) {
          const node = this.visibleNodes[this.focusedNodeIndex];
          if (node) {
            event.preventDefault();
            if (node.expandable) {
              this.treeControl.toggle(node);
              this.cdRef.detectChanges();
            } else {
              this.onFileSelected(node);
            }
          }
        } else if (this.focusCycle === 'refresh') {
          event.preventDefault();
          this.onRefreshIngestFilesList();
        } else if (this.focusCycle === 'home') {
          event.preventDefault();
          this.onHomeClicked();
        } else if (this.focusCycle === 'cancel') {
          event.preventDefault();
          this.dialogRef.close();
        } else {
          this.onIngestFilePathSubmit();
        }
        break;
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          this.cycleTabFocusReverse();
        } else {
          this.cycleTabFocus();
        }
        break;
      case 'ArrowDown':
        if (this.focusCycle === 'list') {
          event.preventDefault();
          this.navigateList(1);
        }
        break;
      case 'ArrowUp':
        if (this.focusCycle === 'list') {
          event.preventDefault();
          this.navigateList(-1);
        }
        break;
    }
  }

  onPathPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') ?? '').trim();
    if (!pasted) { return; }

    // Rule 1: strip filename if path ends with one
    const lastSep = Math.max(pasted.lastIndexOf('\\'), pasted.lastIndexOf('/'));
    const lastSegment = lastSep >= 0 ? pasted.substring(lastSep + 1) : pasted;
    const cleanPath = lastSegment.includes('.') ? pasted.substring(0, lastSep + 1) : pasted;
    this.ingestFilePath = cleanPath;
  }

  dataSourceTypeId?: DataSourceType;
  supportedFileTypes?: string[];
  fileType?: string;
  isUseFtp?: boolean;
  ftpService?: FtpService;
  ingestFilePath: string = '';
  ingestDataConnectorType: number = 0;
  selectedFileNamePath: string = '';
  defaultIngestPath: string = '';
  sessionHomePath: string = '';
  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;
  fullDatasource = [];
  filteredDatasource = [];

  isLoading = new BehaviorSubject<boolean>(false);

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('pathInput') pathInputRef: ElementRef;
  @ViewChild('refreshBtn') refreshBtnRef: ElementRef;
  @ViewChild('homeBtn') homeBtnRef: ElementRef;
  @ViewChild('fileList') fileListRef: ElementRef;
  @ViewChild('openBtn') openBtnRef: ElementRef;
  @ViewChild('cancelBtn') cancelBtnRef: ElementRef;
  files = [];
  focusedNodeIndex = -1;
  focusCycle: 'input' | 'refresh' | 'home' | 'list' | 'open' | 'cancel' = 'input';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<FileBrowserDialogComponent>,
    private configService: ConfigService,
    private cdRef: ChangeDetectorRef,
    private userData: UserDataService,
    private sharedService: SharedService,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService) {

    super(errorLogger, matDialog);

    this.dataSourceTypeId = data.dataSourceTypeId;
    this.supportedFileTypes = data.supportedFileTypes;
    this.fileType = this.supportedFileTypes?.length > 0 ? this.supportedFileTypes[0] : null;
    this.isUseFtp = data.isUseFtp ?? false;
    this.ftpService = data.ftpService ?? null;
    this.ingestDataConnectorType = Number(data.ingestDataConnectorType) ?? 0;
    const rawPath: string = data.ingestFilePath ?? '';
    if (rawPath) {
      const lastSep = Math.max(rawPath.lastIndexOf('\\'), rawPath.lastIndexOf('/'));
      const lastSegment = lastSep >= 0 ? rawPath.substring(lastSep + 1) : rawPath;
      const resolved = lastSegment.includes('.') ? rawPath.substring(0, lastSep + 1) : rawPath;
      this.ingestFilePath = resolved.replace(/\\\\$/, '\\');
      this.defaultIngestPath = this.ingestFilePath;
    } else {
      this.ingestFilePath = '';
      
      const dataConnectorTypeName = DataConnectorType[this.ingestDataConnectorType];
      this.getDefaultIngestPath(dataConnectorTypeName);
    }

    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this._getLevel,
      this._isExpandable,
      this._getChildren);

    this.treeControl = new FlatTreeControl<FileFlatNode>(
      this._getLevel,
      this._isExpandable);
  }

  transformer = (node: FileNode, level: number) => {
    return new FileFlatNode(!!node.children, node.path, node.name, level);
  }

  private _getLevel = (node: FileFlatNode) => node.level;
  private _isExpandable = (node: FileFlatNode) => node.expandable;
  private _getChildren = (node: FileNode): Observable<FileNode[]> =>
    observableOf(node.children)

  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  async ngOnInit(): Promise<void> {
    this.invokeBrowseFolder();
    // this.isLoading.next(true);
    // try {
    //   const organizationId = 1;
    //   const request = new BrowseFolderRequest(
    //     this.dataSourceTypeId,
    //     organizationId,
    //     this.isUseFtp,
    //     this.ftpService,
    //     this.supportedFileTypes,
    //   );
    //   const response = await this.configService.browseFilesAsyncNew(request);
    //   console.log(`Response: ${response.items}`);
    //   if(response.errorMessage && response.items.length === 0) {
    //     this.displayWarning("This path is not valid, please correct it and try again", 'Warning', null);
    //   } else {
    //         response.items.forEach((item) => console.log('Item:', item));

    //         this.fullDatasource = [...response.items]
    //           .filter((t) => {
    //             return (t.isFolder && t.children?.length > 0) || !t.isFolder;
    //           })
    //           .map((item) => {
    //             return { ...item, filename: item.name, children: item.children };
    //           });
            
    //         this.filteredDatasource = this.filterNodeRecursive(_.cloneDeep(this.fullDatasource));
          
    //         this.dataSource = new MatTreeFlatDataSource(
    //           this.treeControl,
    //           this.treeFlattener);

    //         this.dataSource.data = this.filteredDatasource;
    //   }
    

    //   // this.virtualScroll.renderedRangeStream.subscribe(range => {
    //   //   //console.log(range, 'range');
    //   //   this.dataSource.data = this.fullDatasource.slice(range.start, range.end);
    //   // });

    // } catch (err) {
    //   this.displayError('Failed to get folders', 'Error', err, (result) => {
    //     this.dialogRef.close();
    //   });
    // } finally {
    //   this.isLoading.next(false);
    // }
  }

  async invokeBrowseFolder(): Promise<void> {
    this.isLoading.next(true);
    try {
      const organizationId = 1;
      const request = new BrowseFolderRequest(
        this.dataSourceTypeId,
        organizationId,
        this.isUseFtp,
        this.ftpService,
        this.supportedFileTypes,
        (this.ingestFilePath.length == 0) ? "" : this.ingestFilePath
      );
      const response = await this.configService.browseFilesAsyncNew(request);
      console.log(`Response: ${response.items}`);
      if(response.errorMessage && response.items.length === 0) {
        this.dataSource = new MatTreeFlatDataSource(
          this.treeControl,
          this.treeFlattener);
        this.dataSource.data = [];
        this.displayWarning("This path is not valid, please correct it and try again", 'Warning', null);
      } else {
            response.items.forEach((item) => console.log('Item:', item));

            this.fullDatasource = [...response.items]
              .filter((t) => {
                return (t.isFolder && t.children?.length > 0) || !t.isFolder;
              })
              .map((item) => {
                return { ...item, filename: item.name, children: item.children };
              });
            
            this.filteredDatasource = this.filterNodeRecursive(_.cloneDeep(this.fullDatasource));
          
            this.dataSource = new MatTreeFlatDataSource(
              this.treeControl,
              this.treeFlattener);

            this.dataSource.data = this.filteredDatasource;
      }
      // this.virtualScroll.renderedRangeStream.subscribe(range => {
      //   //console.log(range, 'range');
      //   this.dataSource.data = this.fullDatasource.slice(range.start, range.end);
      // });

    } catch (err) {
      this.displayError('Failed to get folders', 'Error', err, (result) => {
        this.dialogRef.close();
      });
    } finally {
      this.isLoading.next(false);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.pathInputRef?.nativeElement.focus(), 50);
  }

  async getDefaultIngestPath(dataConnectorTypeName: string) {
    try {
      const organizationId = 1;
      const request = new GetDefaultIngestNodePathRequest(
        this.dataSourceTypeId,
        organizationId,
        this.isUseFtp,
        this.ftpService,
        this.userData.getRefineryId(),
        this.ingestDataConnectorType
      );
      const response = await this.configService.getDefaultIngestNodePath(request);
      if (response.homeOverridePath) {
        // A per-refinery Home was set for this data source type — use it verbatim.
        this.ingestFilePath = response.homeOverridePath;
      } else {
        this.ingestFilePath = response.defaultIngestNodePath + dataConnectorTypeName + '\\';
      }
      this.defaultIngestPath = this.ingestFilePath;
      await this.invokeBrowseFolder();
    } catch (err) {
      this.displayError('Failed to get default ingest path', 'Error', err, (result) => {
      });
    } finally {
      this.isLoading.next(false);
    }
  }

  onFilterChanged(event: any): void {
    this.filteredDatasource = this.filterNodeRecursive(_.cloneDeep(this.fullDatasource));
    this.dataSource.data = this.filteredDatasource;
  }

  filterNodeRecursive(nodes: FileNode[]): FileNode[] {
    const items = [];

    for (const x of nodes) {
      const parts = x.name.split('.');
      const ext = parts[parts.length - 1];

      if (this.fileType.endsWith('*')) {
        const extAny = this.fileType.substring(0, this.fileType.length - 1);
        if ('.' + ext.startsWith(extAny) && !x.isFolder) {
          items.push(x);
        }
      }
      else if (!x.isFolder) {
        items.push(x);
      }
      // else if ('.' + ext === this.fileType && !x.isFolder) {
      //   items.push(x);
      // }

      if (x.children?.length > 0 && x.isFolder) {
        // add filtered children nodes
        const children = this.filterNodeRecursive(x.children);
        if (children.length > 0) {
          // add the folder
          items.push(x);

          x.children = children;
        }
      }
    }

    return items;
  }

  selectedNode: FileFlatNode = null;

  onNodeSingleClick(node: FileFlatNode): void {
    this.selectedNode = node;
    this.selectedFileNamePath = node.path;
    this.focusCycle = 'list';
    const idx = this.visibleNodes.indexOf(node);
    if (idx >= 0) this.focusedNodeIndex = idx;
    if (node.expandable) {
      // Folder clicked — reflect its path in the Ingest Folder Path textbox.
      this.ingestFilePath = node.path;
    }
    this.cdRef.detectChanges();
    this.fileListRef?.nativeElement.focus();
  }

  onIngestFilePathSubmit(): void {
    if (!this.selectedFileNamePath?.trim()) { return; }
    this.dialogRef.close({ selectedFileName: this.selectedFileNamePath.trim() });
  }

  // Folder paths are always used with a trailing backslash.
  private withTrailingBackslash(path: string): string {
    return path.endsWith('\\') ? path : path + '\\';
  }

  onRefreshIngestFilesList(): void {
    if (this.ingestFilePath?.trim()) {
      this.ingestFilePath = this.withTrailingBackslash(this.ingestFilePath.trim());
      this.invokeBrowseFolder();
    } else {
      this.displayWarning("Ingest folder path cannot be empty!", 'Warning', null);
    }
  }

  onHomeClicked(): void {
    // If a Home path has been set this session, return there instead of the org default.
    if (this.sessionHomePath) {
      this.ingestFilePath = this.sessionHomePath;
      this.invokeBrowseFolder();
      return;
    }
    const dataConnectorTypeName = DataConnectorType[this.ingestDataConnectorType];
    this.getDefaultIngestPath(dataConnectorTypeName);
  }

  onSetToHomeClicked(): void {
    let path = this.ingestFilePath?.trim() ?? '';
    // A valid Home for the selected connector must end with that connector's folder
    // (Text/Excel/Feather/Parquet) — the same segment the default Home path ends with.
    const expectedFolder = DataConnectorType[this.ingestDataConnectorType] ?? '';
    const lastSegment = path.replace(/[\\/]+$/, '').split(/[\\/]/).pop() ?? '';
    const isValid = !!expectedFolder && expectedFolder.toLowerCase() === lastSegment.toLowerCase();

    if (!isValid) {
      this.displayWarning(
        `The specified path is invalid. Please provide a valid ${expectedFolder} file path where the path ends with '${expectedFolder}'.`,
        'Warning', null);
      return;
    }

    path = this.withTrailingBackslash(path);
    this.ingestFilePath = path;

    // Make this the Home for the current session immediately...
    this.sessionHomePath = path;
    this.defaultIngestPath = path;
    // ...refresh the directory listing to the new Home path...
    this.invokeBrowseFolder();
    // ...and hand it to ConfigDetailComponent, which owns the refinery config: it
    // stores it in ingestHomePaths for this connector type and saves the refinery
    // (UpsertRefineryConfig), so the Home button returns here in future sessions.
    this.sharedService.sendEvent({
      type: 'set_ingest_home_path',
      path,
      dataConnectorType: this.ingestDataConnectorType
    });
  }

  onFileSelected(node: FileFlatNode): void {
    console.log('you selected ' + node.path + '/' + node.filename);
    this.dialogRef.close({ selectedFileName: node.path });
  }

  get visibleNodes(): FileFlatNode[] {
    if (!this.treeControl?.dataNodes) return [];
    const result: FileFlatNode[] = [];
    for (const node of this.treeControl.dataNodes) {
      if (this.isNodeVisible(node)) result.push(node);
    }
    return result;
  }

  private isNodeVisible(node: FileFlatNode): boolean {
    if (node.level === 0) return true;
    const nodes = this.treeControl.dataNodes;
    const idx = nodes.indexOf(node);
    for (let i = idx - 1; i >= 0; i--) {
      if (nodes[i].level < node.level) {
        return this.treeControl.isExpanded(nodes[i]) && this.isNodeVisible(nodes[i]);
      }
    }
    return true;
  }

  isFocusedNode(node: FileFlatNode): boolean {
    if (this.focusCycle !== 'list' || this.focusedNodeIndex < 0) return false;
    return this.visibleNodes[this.focusedNodeIndex] === node;
  }

  private navigateList(direction: number): void {
    const visible = this.visibleNodes;
    if (visible.length === 0) return;
    this.focusedNodeIndex = Math.max(0, Math.min(visible.length - 1, this.focusedNodeIndex + direction));
    this.selectedNode = visible[this.focusedNodeIndex];
    this.selectedFileNamePath = this.selectedNode?.path ?? '';
    this.cdRef.detectChanges();
    setTimeout(() => document.querySelector('.focused-file')?.scrollIntoView({ block: 'nearest' }), 0);
  }

  private cycleTabFocus(): void {
    switch (this.focusCycle) {
      case 'input':
        this.pathInputRef?.nativeElement.blur();
        this.focusCycle = 'refresh';
        this.refreshBtnRef?.nativeElement.focus();
        break;
      case 'refresh':
        this.focusCycle = 'home';
        this.homeBtnRef?.nativeElement.focus();
        break;
      case 'home':
        if (this.visibleNodes.length > 0) {
          this.focusCycle = 'list';
          if (this.focusedNodeIndex < 0) this.focusedNodeIndex = 0;
          this.fileListRef?.nativeElement.focus();
        } else {
          this.focusCycle = 'open';
          this.openBtnRef?.nativeElement.focus();
        }
        break;
      case 'list':
        this.focusCycle = 'open';
        this.openBtnRef?.nativeElement.focus();
        break;
      case 'open':
        this.focusCycle = 'cancel';
        this.cancelBtnRef?.nativeElement.focus();
        break;
      case 'cancel':
        this.focusCycle = 'input';
        this.pathInputRef?.nativeElement.focus();
        break;
    }
    this.cdRef.detectChanges();
  }

  private cycleTabFocusReverse(): void {
    switch (this.focusCycle) {
      case 'input':
        this.pathInputRef?.nativeElement.blur();
        this.focusCycle = 'cancel';
        this.cancelBtnRef?.nativeElement.focus();
        break;
      case 'cancel':
        this.focusCycle = 'open';
        this.openBtnRef?.nativeElement.focus();
        break;
      case 'open':
        if (this.visibleNodes.length > 0) {
          this.focusCycle = 'list';
          if (this.focusedNodeIndex < 0) this.focusedNodeIndex = 0;
          this.fileListRef?.nativeElement.focus();
        } else {
          this.focusCycle = 'home';
          this.homeBtnRef?.nativeElement.focus();
        }
        break;
      case 'list':
        this.focusCycle = 'home';
        this.homeBtnRef?.nativeElement.focus();
        break;
      case 'home':
        this.focusCycle = 'refresh';
        this.refreshBtnRef?.nativeElement.focus();
        break;
      case 'refresh':
        this.focusCycle = 'input';
        this.pathInputRef?.nativeElement.focus();
        break;
    }
    this.cdRef.detectChanges();
  }
}
